import { Search, Loader2, CheckCircle2, HelpCircle, Clock, AlertCircle } from "lucide-react";
import { fmt, fmtSar, fmtTime } from "./coUtils";

/** Which waiting text a verdict chip shows (message 17 §2.4, mode-aware): «خلال
 *  دقائق» only while the branch is live under its cap. A lookup that found the
 *  branch at `pos_live_daily_cap` (`live_pull: "capped"`) is nightly-only for
 *  the rest of the day, whatever mode came back with it. */
export function waitKey(mode, livePull) {
    return mode === "live_background" && livePull !== "capped" ? "co_pos_wait_live" : "co_pos_wait_nightly";
}

const ICON = { matched: CheckCircle2, not_found: Clock, ambiguous: HelpCircle, unavailable: Clock, checking: Loader2, error: AlertCircle };
const TONE = {
    matched: "text-[color:var(--success,#15803d)] bg-[color:var(--success-soft,rgba(21,128,61,0.08))]",
    not_found: "text-[color:var(--ink-muted)] bg-[color:var(--bg-surface-2)]",
    ambiguous: "text-[color:var(--warning-soft-fg)] bg-[color:var(--warning-soft)]",
    unavailable: "text-[color:var(--ink-muted)] bg-[color:var(--bg-surface-2)]",
    checking: "text-[color:var(--ink-muted)] bg-[color:var(--bg-surface-2)]",
    error: "text-[color:var(--danger)] bg-[color:var(--danger-soft,rgba(220,38,38,0.08))]",
};

/**
 * One chip for a POS verdict — the lookup result under the invoice field
 * and the row chip in «إدخالاتي». `v` is `{match, total_halalas?, pos_time?,
 * pay_state?, min_digits_hint?, already_recorded?, till?, live_pull?}`; `mode`
 * the branch's effective lookup mode (the lookup's own `mode` when it carries
 * one, else the scope's); `minDigits` the gate for the ambiguous text.
 */
export function PosVerdict({ v, mode, minDigits, t, lang, testId, compact = false }) {
    if (!v || !v.match) return null;
    const Icon = ICON[v.match] || Clock;
    const wait = t(waitKey(mode, v.live_pull));
    let text;
    if (v.match === "checking") text = t(v.live ? "co_pos_searching_live" : "co_pos_checking");
    else if (v.match === "error") text = v.message || t("co_offline_error");
    else if (v.match === "matched") {
        const bits = [t("co_pos_matched")];
        if (v.total_halalas != null) bits.push(`${fmtSar(v.total_halalas, lang)} ${lang === "ar" ? "ر.س" : "SAR"}`);
        if (v.pos_time && !compact) bits.push(fmtTime(v.pos_time, lang));
        if (v.pay_state) bits.push(t(`co_pos_pay_state_${v.pay_state}`) === `co_pos_pay_state_${v.pay_state}` ? v.pay_state : t(`co_pos_pay_state_${v.pay_state}`));
        if (v.till?.count) bits.push(t("co_pos_till"));
        text = bits.join(" · ");
    } else if (v.match === "ambiguous") text = fmt(t("co_pos_ambiguous"), { n: v.min_digits_hint || minDigits || 4 });
    else if (v.match === "not_found" && ["pulled", "joined", "fresh"].includes(v.live_pull)) text = `${t("co_pos_not_arrived")} — ${wait}`;
    else if (v.match === "not_found") text = `${t("co_pos_not_found")} — ${wait}`;
    else text = `${t("co_pos_unavailable")} — ${wait}`;
    return (
        <div data-testid={testId} data-match={v.match} data-live-pull={v.live_pull || undefined}
             className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONE[v.match] || ""}`}>
            <Icon className={`h-3.5 w-3.5 shrink-0 ${v.match === "checking" ? "animate-spin" : ""}`} />
            <span className="truncate">{text}</span>
            {v.already_recorded ? (
                <span className="truncate opacity-80">· {t(v.already_recorded.mine ? "co_pos_already_mine" : "co_pos_already_other")}</span>
            ) : null}
        </div>
    );
}

export function PosLookupButton({ onClick, disabled, busy, live, t, testId }) {
    return (
        <button type="button" data-testid={testId} onClick={onClick} disabled={disabled || busy}
                className="inline-flex h-11 shrink-0 items-center gap-1 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 text-xs font-bold text-[color:var(--brand-brown)] transition-[background-color,opacity] duration-150 hover:bg-[color:var(--brand-orange-soft)] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span>{busy && live ? t("co_pos_searching_live") : t("co_pos_lookup")}</span>
        </button>
    );
}
